import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, ArrowLeft, KeyRound } from 'lucide-react';
import { api } from '../../services/api';
import SEO from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';
import { Turnstile } from '../../components/Turnstile';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
  const [turnstileToken, setTurnstileToken] = useState('');
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('auth', key, translatedFallback) ?? translatedFallback;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await api.forgotPassword(email);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <SEO page="forgotPassword" />
        <div className="min-h-screen bg-gray-50">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
              <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
                <CheckCircle className="w-4 h-4" />
                {t('forgotPassword.successBadge', 'Email Sent')}
              </div>
              <EditableElement section="auth" field="check_email_title">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
                  {get('check_email_title', 'Check your', 'forgotPassword.checkEmailTitle')}{' '}
                  <span className="text-accent">{get('check_email_title_highlight', 'email', 'forgotPassword.checkEmailTitleHighlight')}</span>
                </h1>
              </EditableElement>
              <EditableElement section="auth" field="check_email_subtitle">
                <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                  {get('check_email_subtitle', "We've sent password reset instructions to your email", 'forgotPassword.checkEmailSubtitle')}
                </p>
              </EditableElement>
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
                <path d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f9fafb" />
              </svg>
            </div>
          </div>

          {/* Success Card */}
          <div className="relative -mt-24 pb-16">
            <div className="max-w-md mx-auto px-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-fade-in animation-delay-200">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="mt-4 text-gray-600">
                  {get('check_email_message', "We've sent a password reset link to", 'forgotPassword.checkEmailMessage')} <strong>{email}</strong>
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  {get('check_email_hint', "Didn't receive the email? Check your spam folder or", 'forgotPassword.checkEmailHint')}{' '}
                  <button
                    onClick={() => setSuccess(false)}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {get('try_again', 'try again', 'forgotPassword.tryAgain')}
                  </button>
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {get('back_to_login', 'Back to login', 'forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO page="forgotPassword" />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
              <KeyRound className="w-4 h-4" />
              {t('forgotPassword.badge', 'Password Recovery')}
            </div>

            {/* Title */}
            <EditableElement section="auth" field="forgot_title">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
                {get('forgot_title', 'Forgot your', 'forgotPassword.heroTitle')}{' '}
                <span className="text-accent">{get('forgot_title_highlight', 'password?', 'forgotPassword.heroTitleHighlight')}</span>
              </h1>
            </EditableElement>

            {/* Subtitle */}
            <EditableElement section="auth" field="forgot_subtitle">
              <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                {get('forgot_subtitle', "No worries! Enter your email and we'll send you a reset link.", 'forgotPassword.heroSubtitle')}
              </p>
            </EditableElement>
          </div>

          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
              <path d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f9fafb" />
            </svg>
          </div>
        </div>

        {/* Form Card - Overlapping position */}
        <div className="relative -mt-24 pb-16">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fade-in animation-delay-200">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error message */}
                {error && (
                  <div role="alert" className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {get('email_label', 'Email address', 'auth.emailLabel')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder={get('email_placeholder', 'you@example.com', 'auth.emailPlaceholder')}
                    />
                  </div>
                </div>

                {/* Turnstile */}
                {turnstileSiteKey && (
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onVerify={setTurnstileToken}
                  />
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading || (!!turnstileSiteKey && !turnstileToken)}
                  className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:ring-4 focus:ring-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? get('sending', 'Sending...', 'forgotPassword.sending') : get('send_reset_link', 'Send reset link', 'forgotPassword.sendResetLink')}
                </button>

                {/* Back to login */}
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {get('back_to_login', 'Back to login', 'forgotPassword.backToLogin')}
                </Link>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
